/* 가이드 결정적 해시 — DESIGN §3. 발행 시 변경 감지 단위(작은 주제) 해시와
   카테고리/전역 롤업을 계산한다. 앱은 다운로드한 content 의 hash 가 manifest hash 와
   같은지 검증(무결성). 순서(order) 변경도 상위 롤업의 "자식 해시 연결 순서"로 잡힌다.
   Web Crypto(SHA-256)만 사용 — 추가 의존성 없음. */

/* canonical(x): 객체 키를 재귀 정렬한 공백 없는 JSON. 배열 순서는 보존(순서가 의미). */
export function canonical(value) {
  return JSON.stringify(sortDeep(value));
}

function sortDeep(value) {
  if (Array.isArray(value)) return value.map(sortDeep);
  if (value && typeof value === 'object') {
    const out = {};
    for (const k of Object.keys(value).sort()) out[k] = sortDeep(value[k]);
    return out;
  }
  return value;
}

/* sha256Hex(input): UTF-8 문자열 또는 Uint8Array → 소문자 hex. */
export async function sha256Hex(input) {
  const bytes = typeof input === 'string' ? new TextEncoder().encode(input) : input;
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

/* 블록 타입별 계약(DESIGN §2.3) 필드. 해시·발행은 의미 필드만 보고 내부 CMS id 등은 버린다
   → 해시가 내부 id 변동에 안정적이고, 발행 본문이 계약 스키마(§2.3)와 정확히 일치한다. */
const BLOCK_FIELDS = {
  h: ['text'], p: ['text'], li: ['text'], quote: ['text'],
  video: ['url'],
  code: ['kind', 'file'],
  image: ['key', 'w', 'h', 'alt'],
  huenit: ['key', 'name', 'sha256', 'bytes'],   // .huenit 첨부(W145 §계약) — 키+sha 가 canonical(content) 해시에 포함
};

export function sanitizeBlock(block) {
  const b = block || {};
  const out = { type: b.type };
  for (const f of BLOCK_FIELDS[b.type] || []) if (b[f] !== undefined) out[f] = b[f];
  return out;
}

/* 한 언어의 블록 배열을 계약 필드만 남겨 정규화(비배열 → []). smallHash 와 buildContentDoc 가 공유. */
export function sanitizeBlocks(arr) {
  return (Array.isArray(arr) ? arr : []).map(sanitizeBlock);
}

/* 해시·발행에 쓸 언어 코드 집합 — langs(설정) ∪ content 에 실제 존재하는 키. 정렬(결정적). */
function langCodes(langs, content) {
  const set = new Set();
  for (const l of langs || []) if (l && l.code) set.add(l.code);
  for (const k of Object.keys(content || {})) set.add(k);
  return Array.from(set).sort();
}

/* 한 작은 주제의 전 언어 content 에서 image 블록 .key 모음(중복 제거·정렬). */
export function collectImageKeys(small, langs) {
  const content = small.content || {};
  const keys = new Set();
  for (const lang of langCodes(langs, content)) {
    const arr = Array.isArray(content[lang]) ? content[lang] : [];
    for (const b of arr) if (b && b.type === 'image' && b.key) keys.add(b.key);
  }
  return Array.from(keys).sort();
}

/* 작은 주제 hash = sha256(canonical(title) + canonical(정규화 전 언어 content) + join(sorted(imageKeys))). */
export async function smallHash(small, langs) {
  const content = small.content || {};
  const allContent = {};
  for (const lang of langCodes(langs, content)) allContent[lang] = sanitizeBlocks(content[lang]);
  const imageKeys = collectImageKeys(small, langs);
  return await sha256Hex(canonical(small.title || {}) + canonical(allContent) + imageKeys.join(''));
}

/* 큰 주제 hash = sha256(canonical(meta) + join(child small.hash, 순서대로)). 순서는 연결 순서로 반영. */
export async function bigHash(big, langs) {
  const childHashes = [];
  for (const sm of big.small || []) childHashes.push(await smallHash(sm, langs));
  return await sha256Hex(canonical({ icon: big.icon || '', label: big.label || {} }) + childHashes.join(''));
}

/* 카테고리 hash = sha256(canonical(meta) + join(child big.hash, 순서대로)). */
export async function catHash(cat, langs) {
  const childHashes = [];
  for (const bg of cat.big || []) childHashes.push(await bigHash(bg, langs));
  return await sha256Hex(canonical({ color: cat.color || '', label: cat.label || {} }) + childHashes.join(''));
}

/* 전역 version = sha256(join(category.hash, 순서대로)). */
export async function globalVersion(categories, langs) {
  const catHashes = [];
  for (const cat of categories || []) catHashes.push(await catHash(cat, langs));
  return await sha256Hex(catHashes.join(''));
}
